import React, { useState } from 'react';
import styled from 'styled-components';

const ContactContainer = styled.div`
  padding: 30px;
  font-family: 'Ubuntu', sans-serif;
  color: #00ff00;
  background: rgba(0, 0, 0, 0.9);
  height: 100%;
  overflow-y: auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
  border-bottom: 2px solid #00ff00;
  padding-bottom: 20px;
`;

const Title = styled.h1`
  color: #00ffff;
  margin-bottom: 10px;
`;

const NetworkGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactSection = styled.div`
  background: rgba(0, 255, 0, 0.1);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 8px;
  padding: 20px;
`;

const SectionTitle = styled.h3`
  color: #00ffff;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
  padding: 10px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 255, 0, 0.1);
    transform: translateX(5px);
  }
`;

const ContactIcon = styled.div`
  font-size: 24px;
  width: 40px;
  text-align: center;
`;

const ContactInfo = styled.div`
  flex: 1;
`;

const ContactLabel = styled.div`
  color: #cccccc;
  font-size: 12px;
`;

const ContactValue = styled.div`
  color: #00ff00;
  font-weight: bold;
`;

const SocialItem = styled.a`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid transparent;

  &:hover {
    background: rgba(0, 255, 0, 0.15);
    transform: translateX(5px);
    border-color: rgba(0, 255, 0, 0.5);
    box-shadow: 0 0 15px rgba(0, 255, 0, 0.3);
  }
`;

const SocialIcon = styled.div<{ platform: string }>`
  font-size: 28px;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${props => getSocialColor(props.platform)};
  color: #000;
  transition: transform 0.3s ease;

  ${SocialItem}:hover & {
    transform: scale(1.1);
  }
`;

const SocialInfo = styled.div`
  flex: 1;
`;

const SocialPlatform = styled.div`
  color: #00ffff;
  font-weight: bold;
  font-size: 14px;
`;

const SocialHandle = styled.div`
  color: #cccccc;
  font-size: 12px;
  margin-top: 2px;
`;

const SocialStats = styled.div`
  color: #00ff00;
  font-size: 11px;
  margin-top: 4px;
`;

const MessageForm = styled.form`
  background: rgba(0, 255, 0, 0.1);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 8px;
  padding: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  color: #00ffff;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 4px;
  color: #00ff00;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #00ff00;
    box-shadow: 0 0 10px rgba(0, 255, 0, 0.3);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 4px;
  color: #00ff00;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #00ff00;
    box-shadow: 0 0 10px rgba(0, 255, 0, 0.3);
  }
`;

const SubmitButton = styled.button`
  background: rgba(0, 255, 0, 0.2);
  border: 1px solid #00ff00;
  color: #00ff00;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    background: rgba(0, 255, 0, 0.3);
    box-shadow: 0 0 15px rgba(0, 255, 0, 0.4);
  }
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' }>`
  margin-top: 15px;
  padding: 10px;
  border-radius: 4px;
  background: ${props => props.type === 'success' ? 'rgba(0, 255, 0, 0.2)' : 'rgba(255, 0, 0, 0.2)'};
  border: 1px solid ${props => props.type === 'success' ? '#00ff00' : '#ff0000'};
  color: ${props => props.type === 'success' ? '#00ff00' : '#ff0000'};
`;

const getSocialColor = (platform: string): string => {
  const colors: Record<string, string> = {
    github: '#333333',
    instagram: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
    tryhackme: '#212c42',
    linkedin: '#0077b5'
  };
  return colors[platform] || '#00ff00';
};

const ContactNetwork: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const contactInfo = [
    {
      icon: '📧',
      label: 'Email',
      value: 'memukuljoshi@gmail.com',
      action: () => window.open('mailto:memukuljoshi@gmail.com')
    },
    {
      icon: '📱',
      label: 'Phone',
      value: '+91-7078153883',
      action: () => window.open('tel:+917078153883')
    },
    {
      icon: '🎓',
      label: 'Institution',
      value: 'Army Institute of Technology',
      action: null
    },
    {
      icon: '📍',
      label: 'Location',
      value: 'Pune, Maharashtra',
      action: null
    }
  ];

  const socialLinks = [
    {
      platform: 'GitHub',
      icon: '🐙',
      handle: '@HeyMeMukul',
      url: 'https://github.com/HeyMeMukul',
      stats: 'Open Source Projects',
      platformKey: 'github'
    },
    {
      platform: 'Instagram',
      icon: '📸',
      handle: '@heymemukul',
      url: 'https://www.instagram.com/heymemukul/',
      stats: 'Personal Updates',
      platformKey: 'instagram'
    },
    {
      platform: 'TryHackMe',
      icon: '🎯',
      handle: 'CYBERFY',
      url: 'https://tryhackme.com/p/CYBERFY',
      stats: 'Top 1% Ranking',
      platformKey: 'tryhackme'
    },
    {
      platform: 'LinkedIn',
      icon: '💼',
      handle: 'memukuljoshi',
      url: 'https://www.linkedin.com/in/memukuljoshi/',
      stats: 'Professional Network',
      platformKey: 'linkedin'
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate form submission
    setStatus({ type: 'success', message: 'Message sent successfully! I\'ll get back to you soon.' });
    setFormData({ name: '', email: '', subject: '', message: '' });
    
    setTimeout(() => setStatus(null), 5000);
  };

  return (
    <ContactContainer>
      <Header>
        <Title>📡 Contact Network</Title>
        <p>Connect with me across different platforms</p>
      </Header>

      <NetworkGrid>
        <div>
          <ContactSection>
            <SectionTitle>
              📞 Direct Communication
            </SectionTitle>
            {contactInfo.map((contact, index) => (
              <ContactItem key={index} onClick={contact.action || undefined}>
                <ContactIcon>{contact.icon}</ContactIcon>
                <ContactInfo>
                  <ContactLabel>{contact.label}</ContactLabel>
                  <ContactValue>{contact.value}</ContactValue>
                </ContactInfo>
              </ContactItem>
            ))}
          </ContactSection>

          <ContactSection style={{ marginTop: '20px' }}>
            <SectionTitle>
              🌐 Social Networks
            </SectionTitle>
            {socialLinks.map((social, index) => (
              <SocialItem 
                key={index} 
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <SocialIcon platform={social.platformKey}>
                  {social.icon}
                </SocialIcon>
                <SocialInfo>
                  <SocialPlatform>{social.platform}</SocialPlatform>
                  <SocialHandle>{social.handle}</SocialHandle>
                  <SocialStats>{social.stats}</SocialStats>
                </SocialInfo>
              </SocialItem>
            ))}
          </ContactSection>
        </div>

        <MessageForm onSubmit={handleSubmit}>
          <SectionTitle>
            💬 Send Message
          </SectionTitle>
          
          <FormGroup>
            <Label htmlFor="name">Name</Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="subject">Subject</Label>
            <Input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="message">Message</Label>
            <TextArea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
            />
          </FormGroup>

          <SubmitButton type="submit">
            🚀 Send Message
          </SubmitButton>

          {status && (
            <StatusMessage type={status.type}>
              {status.message}
            </StatusMessage>
          )}
        </MessageForm>
      </NetworkGrid>
    </ContactContainer>
  );
};

export default ContactNetwork;
